// Builds the full clinical-data bundle for a child (IEP snapshot, per-goal
// targets overlay with human-readable names, IEP-tracked behaviors, mand items,
// ref behaviors and maintenance-probe history).
//
// This is the SINGLE source of truth for what gets shown on the data-collection
// page AND what gets cached for offline use. Both the online page load and the
// post-login offline caching call this, so the offline cache always reflects
// the latest supervisor edits (behaviors/targets) after each login.

import { getClientActiveTargets, listClientIepVersions, listGoals, listTargets } from 'services/api/iep';
import { listMandItems } from 'services/api/materials';
import { listSkillDomains } from 'services/api/skills';
import { getRefBehaviors, getClientDataCollections } from 'api/dataCollection';

// Tracking statuses that put a MASTERED target into the maintenance-probe rotation
// (kept in step with PROBE_TRACKING_STATUSES in services/clinicalDatasheets.js). A target
// in one of these is re-probed periodically, so — unlike every other mastered target — it
// must survive the "already mastered → drop from collection" gate below, or the Maintenance
// Probe sheet has nothing to show.
const MAINTENANCE_TRACKING_STATUSES = new Set(['weekly_maintenance', 'monthly', 'long_term']);

// Pick the IEP version that is actually in effect TODAY, rather than blindly the
// highest version number. Versions can model successive IEP periods (e.g. V1
// Jun–Sep, V2 Oct–Dec), so a future-dated V2 must not take over data collection
// before its window starts. `list` is newest-first (version_number desc).
//   1. version whose [start, end] window covers today  → highest such version
//   2. else the latest version that has already started (start <= today)
//   3. else fall back to the newest version (all future / no dates)
// Dates come from the top-level iep_start_date/iep_end_date when present, else
// the snapshot (this bundle requests includeSnapshot), so it works either way.
export const selectActiveVersion = (list) => {
  if (!Array.isArray(list) || list.length === 0) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startOf = (v) => v.iep_start_date || v.snapshot_data?.startDate || null;
  const endOf = (v) => v.iep_end_date || v.snapshot_data?.endDate || null;
  const hasStarted = (v) => {
    const s = startOf(v);
    if (!s) return false;
    const sd = new Date(s);
    sd.setHours(0, 0, 0, 0);
    return sd.getTime() <= today.getTime();
  };
  const coversToday = (v) => {
    if (!hasStarted(v)) return false;
    const e = endOf(v);
    if (!e) return true;
    const ed = new Date(e);
    ed.setHours(23, 59, 59, 999);
    return ed.getTime() >= today.getTime();
  };
  return list.find(coversToday) || list.find(hasStarted) || list[0];
};

/**
 * @param {string} cid - client/child id
 * @param {{ sessionAddedTargetIds?: Set<string>, sessionDate?: string|Date }} [opts]
 *   sessionAddedTargetIds: pending targets added during the current session that
 *     should still be usable now. Empty for login caching.
 *   sessionDate: the session's SCHEDULED date. Target visibility is evaluated as
 *     of this date (introduced on/before it, not yet mastered), NOT as of when the
 *     session is actually opened. Falls back to today when absent (no-session /
 *     login-cache access).
 * @returns {Promise<object>} clinical bundle
 */
export const fetchClinicalBundle = async (cid, { sessionAddedTargetIds = new Set(), sessionDate = null } = {}) => {
  // Maintenance-probe history needs nothing from the IEP calls below, so it starts
  // now instead of after the goals -> targets chain; it used to add a full extra
  // round trip (and a 500-record payload) to every session start.
  const maintenancePromise = getClientDataCollections(cid, { datasheet_type_code: 'maintenance_probe', page_size: 500 }).catch((mpErr) => {
    console.warn('[clinicalBundle] maintenance history fetch failed:', mpErr?.message);
    return null;
  });
  const [iepData, mandItems, behaviors, iepVersions] = await Promise.all([
    getClientActiveTargets(cid),
    listMandItems().catch(() => []),
    getRefBehaviors().catch(() => []),
    listClientIepVersions(cid, { includeSnapshot: true }).catch(() => [])
  ]);

  // The version effective for today (not necessarily the highest version number;
  // a future-dated newer version must not pre-empt the currently-active one).
  const latestVersion = selectActiveVersion(iepVersions);
  const iepSnapshot = latestVersion?.snapshot_data || null;
  const iepBehaviorsSnapshot = Array.isArray(iepSnapshot?.behaviors) ? iepSnapshot.behaviors : [];

  let iepTargetsOverlay = null;
  let dialogGoals = [];
  let addTargetIepId = '';
  // Target Tracking sheet: EVERY introduced target (mastered too), grouped by domain
  // on the sheet. Built from the same listTargets calls as the overlay - no extra
  // requests - and cached with the bundle so the sheet still opens offline.
  const trackingTargets = [];

  if (latestVersion?.id && latestVersion?.iep_id) {
    try {
      const [goalsRes, skillDomains] = await Promise.all([
        listGoals(latestVersion.iep_id, { iep_version_id: latestVersion.id, page_size: 200 }),
        listSkillDomains().catch(() => [])
      ]);
      const goalRows = goalsRes?.items || goalsRes || [];
      const domainNameByCode = new Map((Array.isArray(skillDomains) ? skillDomains : []).map((d) => [d.code, d.name]));

      const snapshotGoalsForDialog = Array.isArray(iepSnapshot?.iepGoals) ? iepSnapshot.iepGoals : [];
      const snapshotByCode = new Map(snapshotGoalsForDialog.map((g) => [g.goalId, g]));
      dialogGoals = goalRows
        .filter((g) => g.goal_code)
        .map((g) => {
          const snap = snapshotByCode.get(g.goal_code);
          return {
            goalId: g.goal_code,
            name: snap?.name || snap?.description || g.description || g.goal_code,
            domain: snap?.domain || g.skill_domain_code || '',
            iepGoalRowId: g.id
          };
        });
      addTargetIepId = latestVersion.iep_id;

      // A target is in play for a session as of the session's SCHEDULED date, not
      // when it's actually opened: e.g. a session scheduled 2026-09-05 but conducted
      // 2026-09-10 must NOT show a target introduced 2026-09-08 — that target didn't
      // exist yet when the session was scheduled. Cutoff is the end of the scheduled
      // day so a target introduced ON that day still counts. Falls back to today for
      // no-session / login-cache access.
      const visibilityCutoff = sessionDate ? new Date(sessionDate) : new Date();
      visibilityCutoff.setHours(23, 59, 59, 999);

      const overlay = {};
      await Promise.all(
        goalRows.map(async (g) => {
          if (!g.goal_code) return;
          try {
            const tRes = await listTargets(latestVersion.iep_id, g.id, { page_size: 500 });
            const snap = snapshotByCode.get(g.goal_code);
            const domainCode = String(snap?.domain || g.skill_domain_code || '').replace(/^DOMAIN-/i, '');
            (tRes?.items || tRes || []).forEach((t) => {
              if (t.declined_date) return;
              trackingTargets.push({
                // The raw iep_target row: the sheet's Edit opens the Goals & Targets
                // TargetDialog on it and PUTs it back, exactly like that tab.
                row: t,
                iepId: latestVersion.iep_id,
                goalRowId: g.id,
                goalCode: g.goal_code,
                goalLabel: snap?.name || snap?.description || g.description || g.goal_code,
                domainCode,
                domainName: domainNameByCode.get(domainCode) || domainCode
              });
            });
            // Gate for data collection: an acquisition target shows when it was INTRODUCED
            // on/before the scheduled date (start_date) and NOT yet mastered (no achieved_date).
            // Approval no longer decides session visibility — introduce/mastery dates do.
            // Declined targets are always excluded. Targets added during the current session
            // are an explicit escape hatch so they're usable immediately even before their
            // dates are set. The one exception to "mastered → drop": a target moved into a
            // maintenance tracking status, which the Maintenance Probe sheet re-probes — so it
            // is kept despite having an achieved_date (excluding it left that sheet empty).
            const rows = (tRes?.items || tRes || []).filter((t) => {
              if (t.declined_date) return false;
              if (sessionAddedTargetIds.has(t.id)) return true;
              if (!t.start_date) return false; // never introduced
              if (MAINTENANCE_TRACKING_STATUSES.has((t.tracking_status || '').toLowerCase())) return true; // mastered → maintenance
              if (t.achieved_date) return false; // mastered, not in maintenance
              return new Date(t.start_date) <= visibilityCutoff; // acquisition: introduced on/before scheduled date
            });
            overlay[g.goal_code] = rows.map((t) => ({
              id: t.id,
              target_code: t.target_code || '',
              name: t.title || '',
              description: t.description || '',
              skillId: t.skill_id || '',
              materials: Array.isArray(t.materials) ? t.materials : [],
              status: t.status?.code || 'in_progress',
              trackingStatus: t.tracking_status || 'not_introduced',
              dateIntroduced: t.start_date || '',
              dateMastered: t.achieved_date || '',
              location: t.location || '',
              people: t.people || '',
              stimuli: t.stimuli || '',
              // Default controlling prompts (ref_prompt_level codes) — pre-selected
              // in the Cold Probe / Multi-Trial session sheets for this target.
              defaultControllingPrompts: Array.isArray(t.default_controlling_prompts) ? t.default_controlling_prompts : [],
              // SD (discriminative stimulus) free-text — shown as a tooltip against
              // the target on the session sheets. Carried in the snapshot overlay.
              sdInstruction: t.sd_instruction || ''
            }));
          } catch (tErr) {
            console.warn(`[clinicalBundle] listTargets(${g.goal_code}) failed:`, tErr?.message);
            overlay[g.goal_code] = [];
          }
        })
      );
      iepTargetsOverlay = overlay;
    } catch (goalsErr) {
      console.warn('[clinicalBundle] Failed to load goals/targets from tables:', goalsErr?.message);
    }
  }

  // Last maintenance-probe date/score per target (weekly/monthly/long-term).
  const maintenanceHistory = {};
  try {
    const mpRes = await maintenancePromise;
    const records = mpRes?.items || mpRes || [];
    records.sort((a, b) => new Date(a.collected_at) - new Date(b.collected_at)); // last occurrence wins
    records.forEach((rec) => {
      ((rec.raw_data && rec.raw_data.targets) || []).forEach((t) => {
        if (!t?.targetId || !t?.response) return;
        maintenanceHistory[t.targetId] = {
          lastProbeDate: rec.collected_at ? new Date(rec.collected_at).toLocaleDateString() : null,
          lastProbeScore: t.response === 'correct' ? '✓' : t.response === 'incorrect' ? '✗' : null
        };
      });
    });
  } catch (mpErr) {
    console.warn('[clinicalBundle] maintenance history fetch failed:', mpErr?.message);
  }

  return {
    iepData,
    mandItems,
    behaviors,
    iepBehaviorsSnapshot,
    iepSnapshot,
    iepTargetsOverlay,
    maintenanceHistory,
    addTargetCtx: { iepId: addTargetIepId, goals: dialogGoals },
    trackingTargets,
    // The version whose snapshot the sheets read - where the session's Behaviors
    // shortcut adds/edits behaviors, so they show up on these same sheets.
    iepVersionId: latestVersion?.id || ''
  };
};

/**
 * Shape the bundle into the record persisted by cacheClinicalData.
 */
export const clinicalBundleToCache = (bundle) => ({
  iepData: bundle.iepData,
  mandItems: bundle.mandItems,
  behaviors: bundle.behaviors,
  iepBehaviorsSnapshot: bundle.iepBehaviorsSnapshot,
  iepSnapshot: bundle.iepSnapshot,
  iepTargetsOverlay: bundle.iepTargetsOverlay,
  // Kept so a page drawn from the cache (instantly, before the network answers,
  // or offline) is complete - maintenance dates and the Add Target goal list.
  maintenanceHistory: bundle.maintenanceHistory,
  addTargetCtx: bundle.addTargetCtx,
  trackingTargets: bundle.trackingTargets,
  iepVersionId: bundle.iepVersionId
});

export default fetchClinicalBundle;
